import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Car,
  DollarSign,
  Clock,
  Users,
  Shield,
  CheckCircle2,
  Star,
  Calendar,
  TrendingUp,
  Smartphone,
  ArrowRight,
  ChevronDown,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

// ─── Data ─────────────────────────────────────────────────────────────────────

const BENEFITS = [
  {
    icon: <DollarSign className="h-6 w-6 text-emerald-600" />,
    bg: 'bg-emerald-50',
    title: 'Ganhos acima da média',
    description:
      'Instrutores parceiros faturam em média R$ 4.200/mês. Defina seu próprio preço por hora e aumente conforme sua reputação.',
  },
  {
    icon: <Clock className="h-6 w-6 text-brand-600" />,
    bg: 'bg-brand-50',
    title: 'Horários flexíveis',
    description:
      'Você escolhe quando e quantas aulas quer dar. Trabalhe nos finais de semana, à tarde ou pela manhã — da forma que preferir.',
  },
  {
    icon: <Users className="h-6 w-6 text-violet-600" />,
    bg: 'bg-violet-50',
    title: 'Acesso a milhares de alunos',
    description:
      'Nossa plataforma conecta você a alunos na sua cidade que estão ativamente buscando um instrutor como você.',
  },
  {
    icon: <Shield className="h-6 w-6 text-orange-600" />,
    bg: 'bg-orange-50',
    title: 'Pagamento garantido',
    description:
      'Receba pelo Mercado Pago com segurança. O pagamento é processado antes da aula e depositado na sua conta após a conclusão.',
  },
  {
    icon: <TrendingUp className="h-6 w-6 text-indigo-600" />,
    bg: 'bg-indigo-50',
    title: 'Cresça sua reputação',
    description:
      'Boas avaliações aumentam sua visibilidade na plataforma. Quanto mais alunos satisfeitos, mais agendamentos você recebe.',
  },
  {
    icon: <Smartphone className="h-6 w-6 text-teal-600" />,
    bg: 'bg-teal-50',
    title: 'Gestão simplificada',
    description:
      'Gerencie agendamentos, pagamentos e comunicação com alunos em um único lugar — sem burocracia.',
  },
]

const REQUIREMENTS = [
  {
    icon: <Car className="h-5 w-5 text-brand-600" />,
    text: 'CNH categoria B ou superior (com EAR, se aplicável)',
  },
  {
    icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    text: 'Certificado de instrutor de trânsito (CIRETRAN)',
  },
  {
    icon: <Car className="h-5 w-5 text-gray-600" />,
    text: 'Veículo próprio em bom estado de conservação',
  },
  {
    icon: <Shield className="h-5 w-5 text-orange-600" />,
    text: 'Documentação do veículo regularizada (IPVA, seguro)',
  },
  {
    icon: <Users className="h-5 w-5 text-violet-600" />,
    text: 'Celular com acesso à internet para gerenciar agendamentos',
  },
  {
    icon: <Star className="h-5 w-5 text-yellow-600" />,
    text: 'Comprometimento com a qualidade e pontualidade',
  },
]

const PROCESS_STEPS = [
  {
    number: 1,
    title: 'Crie sua conta',
    description: 'Cadastre-se gratuitamente como instrutor em menos de 2 minutos.',
    color: 'bg-brand-600',
  },
  {
    number: 2,
    title: 'Complete seu perfil',
    description:
      'Adicione sua bio, especialidades, fotos, horários disponíveis e valor por hora.',
    color: 'bg-indigo-600',
  },
  {
    number: 3,
    title: 'Verificação de documentos',
    description:
      'Nossa equipe analisa seus documentos em até 48 horas úteis para garantir a qualidade da plataforma.',
    color: 'bg-violet-600',
  },
  {
    number: 4,
    title: 'Apareça nas buscas',
    description:
      'Seu perfil fica visível para alunos da sua cidade assim que a verificação for concluída.',
    color: 'bg-emerald-600',
  },
  {
    number: 5,
    title: 'Receba agendamentos',
    description:
      'Alunos entram em contato, você confirma, dá a aula e recebe o pagamento direto na sua conta.',
    color: 'bg-teal-600',
  },
]

// ─── Earnings calculator ──────────────────────────────────────────────────────

function EarningsCalculator() {
  const [classesPerWeek, setClassesPerWeek] = useState(15)
  const [hourlyRate, setHourlyRate] = useState(100)

  const monthly = classesPerWeek * 4 * hourlyRate
  const platformFee = monthly * 0.1
  const netEarnings = monthly - platformFee

  const format = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <h3 className="font-bold text-gray-900">Calculadora de ganhos</h3>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-gray-700">
              Aulas por semana
            </Label>
            <span className="text-lg font-bold text-brand-600">{classesPerWeek}</span>
          </div>
          <input
            type="range"
            min={1}
            max={40}
            value={classesPerWeek}
            onChange={(e) => setClassesPerWeek(Number(e.target.value))}
            className="w-full accent-brand-600"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>1 aula/sem</span>
            <span>40 aulas/sem</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-gray-700">
              Valor por hora (R$)
            </Label>
            <span className="text-lg font-bold text-brand-600">
              R$ {hourlyRate}
            </span>
          </div>
          <input
            type="range"
            min={50}
            max={300}
            step={10}
            value={hourlyRate}
            onChange={(e) => setHourlyRate(Number(e.target.value))}
            className="w-full accent-brand-600"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>R$ 50/h</span>
            <span>R$ 300/h</span>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Faturamento bruto/mês</span>
            <span className="font-medium text-gray-800">{format(monthly)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Taxa da plataforma (10%)</span>
            <span className="font-medium text-red-500">- {format(platformFee)}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900">Ganho líquido/mês</span>
            <span className="text-2xl font-bold text-emerald-600">
              {format(netEarnings)}
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-400">
          * Estimativa baseada em aulas de 1 hora. Valores aproximados para
          fins ilustrativos.
        </p>
      </div>
    </div>
  )
}

// ─── Testimonial ──────────────────────────────────────────────────────────────

interface TestimonialProps {
  name: string
  city: string
  photo?: string
  rating: number
  text: string
  earnings: string
}

function Testimonial({ name, city, rating, text, earnings }: TestimonialProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`h-4 w-4 ${
              s <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        ))}
      </div>
      <p className="text-gray-600 leading-relaxed italic">"{text}"</p>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-gray-900">{name}</p>
          <p className="text-sm text-gray-400">{city}</p>
        </div>
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
          {earnings}/mês
        </Badge>
      </div>
    </div>
  )
}

const TESTIMONIALS: TestimonialProps[] = [
  {
    name: 'Carlos Mendonça',
    city: 'São Paulo, SP',
    rating: 5,
    text: 'Em 6 meses no MeuDriver já tenho agenda cheia. Triplicou minha renda comparado a quando trabalhava de forma independente.',
    earnings: 'R$ 5.800',
  },
  {
    name: 'Ana Lima',
    city: 'Curitiba, PR',
    rating: 5,
    text: 'A plataforma é fácil de usar e os alunos chegam até mim. Consigo conciliar com meu horário de trabalho na autoescola.',
    earnings: 'R$ 3.200',
  },
  {
    name: 'Roberto Fonseca',
    city: 'Belo Horizonte, MG',
    rating: 5,
    text: 'O pagamento seguro foi o diferencial pra mim. Nunca mais tive problemas com inadimplência. Recomendo para todos os colegas.',
    earnings: 'R$ 4.500',
  },
]

// ─── Main component ───────────────────────────────────────────────────────────

export default function BeInstructorPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800 text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-white/20 text-white border-0">
                Para instrutores
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
                Transforme seu talento em{' '}
                <span className="text-teal-200">renda extra</span>
              </h1>
              <p className="text-xl text-teal-100 leading-relaxed">
                Cadastre-se gratuitamente no MeuDriver e comece a receber alunos
                ainda este mês. Sem mensalidade, sem burocracia.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-teal-700 hover:bg-teal-50 font-semibold px-8 w-full sm:w-auto"
                >
                  <Link to="/cadastro?role=instructor">
                    Cadastrar como instrutor
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/40 text-white hover:bg-white/10"
                >
                  <Link to="/como-funciona">Como funciona?</Link>
                </Button>
              </div>

              <div className="flex flex-wrap gap-4 pt-2">
                {[
                  '✓ Cadastro grátis',
                  '✓ Sem mensalidade',
                  '✓ Receba em 24h',
                ].map((item) => (
                  <span key={item} className="text-teal-100 text-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Calculator */}
            <div className="lg:pl-4">
              <EarningsCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* Stats banner */}
      <section className="bg-gray-900 text-white py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '800+', label: 'Instrutores ativos' },
              { value: 'R$ 4.200', label: 'Ganho médio/mês' },
              { value: '12k+', label: 'Alunos na plataforma' },
              { value: '4.9/5', label: 'Avaliação média' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-bold text-teal-400">{stat.value}</p>
                <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">
            Por que se tornar um instrutor MeuDriver?
          </h2>
          <p className="mt-3 text-gray-500 text-lg max-w-2xl mx-auto">
            Mais do que uma plataforma — um parceiro para o crescimento da sua
            carreira.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b) => (
            <div
              key={b.title}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-3 hover:shadow-md transition-shadow"
            >
              <div className={`rounded-xl p-3 w-fit ${b.bg}`}>{b.icon}</div>
              <h3 className="font-semibold text-gray-900">{b.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{b.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="secondary" className="mb-4">
                Requisitos
              </Badge>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                O que você precisa para se cadastrar
              </h2>
              <p className="text-gray-500 leading-relaxed">
                Para garantir a segurança dos nossos alunos, exigimos
                documentação básica. O processo de verificação é simples e
                leva até 48 horas úteis.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              {REQUIREMENTS.map((req, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="rounded-lg bg-gray-50 p-1.5 flex-shrink-0 mt-0.5">
                    {req.icon}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{req.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-step process */}
      <section className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">
            Como se tornar um instrutor parceiro
          </h2>
          <p className="mt-3 text-gray-500 text-lg">
            Do cadastro ao primeiro agendamento em menos de uma semana.
          </p>
        </div>
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-5 top-10 bottom-10 w-px bg-gray-200 hidden sm:block" />
          <div className="space-y-8">
            {PROCESS_STEPS.map((step) => (
              <div key={step.number} className="flex gap-5">
                <div
                  className={`h-10 w-10 rounded-full ${step.color} text-white flex items-center justify-center font-bold flex-shrink-0 z-10 text-sm shadow-md`}
                >
                  {step.number}
                </div>
                <div className="pt-1.5 pb-6">
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 mt-1 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              O que dizem nossos instrutores
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <Testimonial key={t.name} {...t} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-teal-600 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Pronto para aumentar sua renda?
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Mais de 800 instrutores já estão lucrando com o MeuDriver.
            Cadastro gratuito, sem mensalidade.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-teal-700 hover:bg-teal-50 font-bold px-12 text-lg h-14"
          >
            <Link to="/cadastro?role=instructor">
              Cadastrar como instrutor agora
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </Button>
          <p className="text-teal-200 text-sm mt-4">
            Grátis · Sem cartão de crédito · Comece em minutos
          </p>
        </div>
      </section>
    </div>
  )
}
